import {inject, Injectable} from '@angular/core';
import {v4 as uuidv4} from 'uuid';
import * as amplitude from '@amplitude/analytics-browser';

import {environment} from "../../../environments/environment";
import {NavigationEnd, Router} from "@angular/router";
import {UserService} from "./user.service";
import {distinctUntilChanged, filter, map, merge, take} from "rxjs";
import {sessionReplayPlugin} from "@amplitude/plugin-session-replay-browser";
import {BotDetectionService} from "./bot-detection.service";
import {PlatformService} from "./platform.service";
import {platform} from "../utils";
import {AnalyticsService, AmplitudeEvent, PaymentEventProperties, TikTokPayload} from "./analytics.service";
import {ActiveUserDto, MandatoryFlowUserDto, SignupFlowUserDto} from "homey-housing-lottery-app-dto";

const isBrowserEnvironment =
  typeof document !== 'undefined' &&
  typeof window !== 'undefined';
const amplitudeReturn: amplitude.Types.AmplitudeReturn<void> | null = isBrowserEnvironment
  ? amplitude.init(environment.amplitudeKey, {
      serverUrl: environment.amplitudeServerUrl,
      autocapture: {elementInteractions: true},
      logLevel: amplitude.Types.LogLevel.Warn,
    })
  : null;

const platformMap: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};

const mappedPlatform = platformMap[platform] ?? 'Unknown';

const platformPlugin: amplitude.Types.EnrichmentPlugin = {
  name: 'PlatformPlugin',
  type: 'enrichment',
  /** No one‑time setup needed here */
  setup: async () => {},

  /** Called with each event; must return the (possibly mutated) event */
  execute: async (event) => {
    event.platform = mappedPlatform;
    return event;
  },
};

if (isBrowserEnvironment) {
  amplitude.add(platformPlugin);
}


@Injectable({
  providedIn: 'root'
})
export class BrowserAnalyticsService extends AnalyticsService {
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly botDetectionService = inject(BotDetectionService);
  private lastTikTokIdentifiedUserId?: string;

  public async sendEvent(event: AmplitudeEvent, properties?: Record<string, string | number | undefined>) {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    try {
      await amplitudeReturn.promise;
      await amplitude.track(`[Homey] ${event}`, properties).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to send event', e);
    }

    const fbEventName = this.mapToFacebookEventName(event);
    if (fbEventName) {
      this.trackFacebookEvent(fbEventName);
    }

    const ttEventNames = this.mapToTikTokEventNames(event);
    if (ttEventNames.length > 0) {
      ttEventNames.forEach(name => this.trackTikTokEvent(name, properties));
    }

    const googleEventName = this.mapToGoogleAdsEventName(event);
    if (googleEventName && environment.googleAdsId) {
      this.trackGoogleAdsEvent(googleEventName);
    }

  }

  public mapToFacebookEventName(event: AmplitudeEvent): string | null {
    switch (event) {
      case 'signup-success':
        return 'CompleteRegistration';
      case 'payment-success':
        return 'Purchase';
      case 'payment-started':
        return 'InitiateCheckout';
      case 'benefits-page-viewed':
        return 'ViewContent';
      default:
        return null;
    }
  }

  public mapToTikTokEventNames(event: AmplitudeEvent): string[] {
    switch (event) {
      case 'signup-success':
        return ['CompleteRegistration'];
      case 'payment-success':
        return ['Purchase', 'CompletePayment'];
      case 'payment-started':
      case 'resubscribe-payment-started':
        return ['InitiateCheckout', 'AddPaymentInfo'];
      case 'benefits-page-viewed':
        return ['ViewContent'];
      default:
        return [];
    }
  }

  public mapToGoogleAdsEventName(event: AmplitudeEvent): string | null {
    switch (event) {
      case 'payment-started':
        return 'begin_checkout';
      case 'benefits-page-viewed':
        return 'view_content';
      default:
        return null;
    }
  }

  public async setClickSource(source: string, url: string, variant?: string) {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    try {
      const eventProps: Record<string, string | undefined> = {
        click_source: source,
        url: url,
        click_variant: variant,
      };

      this.sendEvent('click-tracked', eventProps);
      await amplitudeReturn.promise;
      const identify = new amplitude.Identify().set('click_source', source);
      if (variant) {
        identify.set('click_variant', variant);
      }
      await amplitude.identify(identify).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to set click campaign', e);
    }
  }

  private trackGoogleAdsEvent(eventName: string) {
    if (this.platformService.isServer) {
      return;
    }

    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== 'function') {
      return;
    }

    try {
      gtag('event', eventName, {
        send_to: environment.googleAdsId,
      });
    } catch (err) {
      console.error('Google Ads track error', err);
    }
  }

  public buildPaymentEventProperties(
    base: {
      flow: PaymentEventProperties['flow'];
      checkoutLocation: PaymentEventProperties['checkoutLocation'];
      interval: PaymentEventProperties['interval'];
    }
  ): PaymentEventProperties {
    return {
      ...base,
      interval: base.interval ?? 'unknown',
    };
  }

  public async sendPaymentStarted(params: {
    flow: 'initial' | 'resubscribe',
    interval: 'monthly' | 'annual',
    checkoutLocation: 'default' | 'deferred' | 'deferred-no-trial',
    amount: number,
  }) {
    try {
      const event = params.flow === 'resubscribe' ? 'resubscribe-payment-started' : 'payment-started';
      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        interval: params.interval,
        checkoutLocation: params.checkoutLocation,
      });

      await this.sendEvent(event, {...properties, amount: params.amount});
    } catch (err) {
      console.error('sendPaymentStarted analytics error', err);
    }
  }

  public async sendPaymentSuccess(params: {
    flow: 'initial' | 'resubscribe',
    checkoutLocation: 'default' | 'deferred' | 'deferred-no-trial',
    interval: PaymentEventProperties['interval'],
    amount?: number,
  }) {
    try {
      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        checkoutLocation: params.checkoutLocation,
        interval: params.interval,
      });
      await this.sendEvent('payment-success', {...properties, amount: params.amount});
    } catch (err) {
      console.error('sendPaymentSuccess analytics error', err);
    }
  }

  public getUTMData(): {utmSource: string, utmCampaign: string} {
    const prefix = environment.amplitudeKey.slice(0, 10);
    const cookieName = `AMP_MKTG_${prefix}`;
    let utmSource = 'unknown';
    let utmCampaign = 'unknown';
    if (!isBrowserEnvironment) {
      return {utmSource, utmCampaign};
    }

    try {
      // grab the cookie value
      const raw = document.cookie
        .split('; ')
        .find((c) => c.startsWith(`${cookieName}=`))
        ?.split('=')[1];

      if (raw) {
        // parse JSON safely
        const uriEncoded = atob(raw);                         // :contentReference[oaicite:1]{index=1}
        const data = JSON.parse(decodeURIComponent(uriEncoded));
        // pick out only what you need, falling back to defaults
        utmSource   = data.utm_source   ?? utmSource;
        utmCampaign = data.utm_campaign ?? utmCampaign;
      }
    } catch (err) {
      // swallow any errors—defaults will stay in place
      console.error('getUTMData error:', err);
    }

    return { utmSource, utmCampaign };
  }

  public initialize() {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    try {
      amplitudeReturn.promise.then(() => {
          this.userService.user$.pipe(
            map(user => user.status !== 'unauthorized' ? user.id : undefined),
            filter(id => !!id),
            distinctUntilChanged(),
          ).subscribe(id => {
            amplitude.setUserId(id);
            this.identifyTikTokUserIfNeeded();
          });

          amplitude.flush();

          this.setupSessionRecording();

          window.addEventListener('pagehide',
            () => {
              amplitude.setTransport('beacon')
              amplitude.flush()
            },
          );
        }
      )
    } catch (e) {
      console.error('Failed to initialize Amplitude', e);
    }
  }

  private async identifyTikTokUserIfNeeded() {
    if (this.platformService.isServer) return;

    const user = this.getKnownUser();
    if (!user) return;

    if (user.id === this.lastTikTokIdentifiedUserId) return;

    const ttq = (window as unknown as { ttq?: { identify?: (payload: unknown) => void } }).ttq;
    if (!ttq || typeof ttq.identify !== 'function') return;

    const externalId = user.id?.trim();
    const hashedExternalId = externalId ? await this.sha256(externalId) : undefined;

    if (!hashedExternalId) return;

    ttq.identify({
      external_id: hashedExternalId,
    });

    this.lastTikTokIdentifiedUserId = user.id;
  }

  private getKnownUser(): (ActiveUserDto | MandatoryFlowUserDto | SignupFlowUserDto) | undefined {
    const user = this.userService.user();
    if (!user || user.status === 'unauthorized') return undefined;
    return user;
  }

  private async sha256(value: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  async setupSessionRecording() {
    if (environment.amplitude.sessionRecordingEnabled) {
      try {
        const bot = await this.botDetectionService.isBot();

        if (bot) {
          return;
        }

        const relevantPage = this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          filter(event => event.url.includes('signup') || event.url.includes('login')),
        );

        const loggedInUser = this.userService.user$.pipe(
          filter(user => user.status !== 'unauthorized'),
        );

        merge(relevantPage, loggedInUser).pipe(
          take(1),
        ).subscribe(() => {
          amplitude.add(
            sessionReplayPlugin({
              sampleRate: 1,
            }),
          );
        });

      } catch (e) {
        console.error('Failed to setup session recording', e);
      }
    }
  }

  private trackTikTokEvent(
    eventName: string,
    properties?: Record<string, string | number | undefined>,
  ) {
    if (this.platformService.isServer) {
      return;
    }

    const ttq = (window as unknown as { ttq?: { track?: (event: string, payload?: unknown) => void } }).ttq;
    if (!ttq || typeof ttq.track !== 'function') {
      return;
    }

    try {
      const payload = this.buildTikTokPayload(eventName, properties);
      if (Object.keys(payload).length > 0) {
        ttq.track(eventName, payload);
      } else {
        ttq.track(eventName);
      }
    } catch (err) {
      console.error('TikTok pixel track error', err);
    }
  }

  private buildTikTokPayload(
    eventName: string,
    properties?: Record<string, string | number | undefined>,
  ): TikTokPayload {
    const payload: TikTokPayload = {};
    const defaultValue = 15; // ROAS assumption: flat $15 subscription price

    const value = typeof properties?.['amount'] === 'number'
      ? properties['amount']
      : typeof properties?.['value'] === 'number'
        ? properties['value']
        : defaultValue;

    if (value !== undefined) {
      payload.value = value;
      payload.currency = 'USD';
      payload.contents = [{
        content_id: 'homey_subscription',
        content_type: 'product',
        content_name: 'Homey subscription',
        quantity: 1,
        price: value,
      }];
    }

    const searchString = properties?.['search_string'] ?? properties?.['search'];
    if (typeof searchString === 'string') {
      payload.search_string = searchString;
    }

    // Provide an event_id for deduplication with future server sends
    const eventIdProp = properties?.['event_id'];
    payload.event_id = typeof eventIdProp === 'string' ? eventIdProp : this.generateEventId(eventName);

    // Basic context
    payload.context = {
      page: {
        url: typeof location !== 'undefined' ? location.href : undefined,
      },
      user: {
        // _ttp improves attribution; safe to read cookie if present
        _ttp: this.getCookie('_ttp'),
      },
    };

    // Strip undefined/null to avoid noise
    Object.keys(payload).forEach((key) => {
      const valueAtKey = (payload as Record<string, unknown>)[key];
      if (valueAtKey === undefined || valueAtKey === null) {
        delete (payload as Record<string, unknown>)[key];
      }
    });

    return payload;
  }

  private generateEventId(eventName: string): string {
    return uuidv4();
  }

  private getCookie(name: string): string | undefined {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`));
    return match?.split('=')[1];
  }

  private trackFacebookEvent(name: string) {
    if (this.platformService.isServer) {
      return;
    }

    const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq;
    if (typeof fbq !== 'function') {
      return;
    }

    try {
      fbq('track', name);
    } catch (err) {
      console.error('Facebook pixel track error', err);
    }
  }
}
